import { useHeaderHeight } from '@react-navigation/elements';
import { useNavigation } from '@react-navigation/native';
import { Banner, BaseText, Meta, Spinner, theme, Title } from 'aimo-ui';
import VehicleAddSection from 'components/account/vehicle/VehicleAddSection';
import VehicleAvatar from 'components/common/VehicleAvatar';
import VehicleLicensePlate from 'components/common/VehicleLicensePlate';
import Icon from 'components/icons/Icon';
import AccountContext from 'context/AccountContext';
import useGetAvailableAnprPaymentOption from 'hooks/useGetAvailableAnprPaymentOption';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { useVehicleStore } from 'stores/vehicleStore';
import styled from 'styled-components/native';
import { Pill } from 'styles/CommonLayoutStyles';
import {
  CenterVerticalRow,
  ColumnWithMargins,
  ScreenContainer,
  ScreenWrapper,
  SheetDetailsWrapper,
  StyledImage,
  StyledWrapper,
} from 'styles/ContainerStyles';
import { Vehicle } from 'types/generatedSchemaTypes';
import { AccountNavProps } from 'types/navigation';
import { isWeb } from 'utils/deviceUtils';
import { getPermitsAmount } from 'utils/permitUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import { isVehicleLowEmission } from 'utils/vehicleUtils';

const VehicleWrapper = styled.View<{ showDivider: boolean }>`
  flex-direction: row;
  align-items: center;
  padding-bottom: 17px;
  margin-bottom: 16px;
  ${(props) =>
    props.showDivider &&
    `border-bottom-width: 3px;
    border-bottom-color: ${theme.colors.gray50};`}
`;

const VehicleInfoWrapper = styled.View`
  flex-direction: column;
  margin-left: 20px;
`;

const IconWrapper = styled.View`
  margin-left: auto;
  margin-right: 10px;
  justify-content: center;
`;

const TrunckatedText = styled(BaseText)`
  flex-shrink: 1;
`;

const Row = styled.View`
  align-items: center;
  flex-direction: row;
`;

const NoVehicles: React.FC<{ navigate: () => void; headerHeight: number }> = ({
  navigate,
  headerHeight,
}) => {
  const { t } = useTranslation();

  return (
    <SheetDetailsWrapper
      pt={isWeb ? headerHeight : theme.normalising.heightPixel(30)}
      testID="no-content-container">
      <StyledWrapper center>
        <StyledImage
          testID="vehicles-no-content-image"
          width={183}
          height={152}
          center
          source={require('assets/images/parking-receipt-car.png')}
        />
      </StyledWrapper>
      <StyledWrapper
        center
        mb={theme.normalising.heightPixel(37)}
        maxWidth={240}>
        <Title center testID="vehicles-no-content-title">
          {t('vehicle.noContentTitle')}
        </Title>
      </StyledWrapper>
      <VehicleAddSection onPress={navigate} style="BUTTON" />
    </SheetDetailsWrapper>
  );
};

const WarningIcon = () => (
  <Icon name="Info-Outlined" color={theme.colors.aimoUiRed} />
);

const MyVehiclesScreen: React.FC = () => {
  const { navigate } = useNavigation<AccountNavProps>();
  const headerHeight = useHeaderHeight();
  const {
    customer: { vehicles, permits, licensePlateBasedPermits },
    loading,
  } = useContext(AccountContext);
  const { t } = useTranslation();
  const anprPaymentOption = useGetAvailableAnprPaymentOption();
  const { resetVehicleStore, setVehicleData } = useVehicleStore(
    (state) => state
  );

  const handleSelectVehicle = (v: Vehicle) => {
    resetVehicleStore();
    setVehicleData(v);
    navigate('editVehicle', { licensePlate: v.licensePlate?.plateText });
  };

  const isCamerarecognitionVehicleAvailable = !!vehicles?.find(
    (vehicle) => vehicle.cameraRecognition?.enabled
  );

  const navigateToAddVehicle = () => {
    resetVehicleStore();
    navigate('addVehicleAccount');
  };

  if (loading) {
    return (
      <ScreenContainer>
        <ScreenWrapper tabBarHeight={headerHeight}>
          <StyledWrapper center>
            <Spinner testID="my-vehicles-loader" />
          </StyledWrapper>
        </ScreenWrapper>
      </ScreenContainer>
    );
  }

  if (!vehicles || !vehicles.length) {
    return (
      <NoVehicles navigate={navigateToAddVehicle} headerHeight={headerHeight} />
    );
  }

  return (
    <ScrollView>
      <SheetDetailsWrapper
        pt={isWeb ? headerHeight : theme.normalising.heightPixel(30)}>
        <StyledWrapper mb={10}>
          {anprPaymentOption === 'NO_OPTIONS' &&
            isCamerarecognitionVehicleAvailable && (
              <Pressable onPress={() => navigate('myPaymentMethods')}>
                <StyledWrapper mb={20}>
                  <Banner
                    text={t('vehicle.cameraRecognition.tutorial.noPayment')}
                    type="warning"
                    iconComponent={WarningIcon}
                  />
                </StyledWrapper>
              </Pressable>
            )}
          {vehicles?.map((vehicle, index) => (
            <Pressable
              accessibilityRole="button"
              key={`${vehicle.licensePlate?.plateText}-${index}`}
              onPress={() => handleSelectVehicle(vehicle)}
              {...testIdentifiers(
                `edit-vehicle-${vehicle.licensePlate?.plateText}`
              )}>
              <VehicleWrapper showDivider={index !== vehicles.length - 1}>
                <VehicleAvatar
                  color={vehicle.appSettings?.color}
                  icon={vehicle.appSettings?.icon}
                  size="l"
                  isLowEmission={isVehicleLowEmission(
                    licensePlateBasedPermits,
                    vehicle
                  )}
                />
                <VehicleInfoWrapper>
                  <StyledWrapper mb={0} maxWidth={230}>
                    <TrunckatedText
                      numberOfLines={1}
                      {...testIdentifiers(
                        `vehicle-name-${vehicle.licensePlate?.plateText}`
                      )}>
                      {vehicle.name || vehicle.licensePlate?.plateText}
                    </TrunckatedText>
                  </StyledWrapper>
                  <CenterVerticalRow>
                    <VehicleLicensePlate licensePlate={vehicle.licensePlate} />
                    {getPermitsAmount(
                      permits,
                      vehicle.licensePlate?.plateText
                    ) > 0 && (
                      <Meta color={theme.colors.gray300}>
                        {` ・ ${t('vehicle.permit', {
                          count: getPermitsAmount(
                            permits,
                            vehicle.licensePlate?.plateText
                          ),
                        })}`}
                      </Meta>
                    )}
                  </CenterVerticalRow>
                </VehicleInfoWrapper>
                <IconWrapper>
                  <Row>
                    {vehicle?.cameraRecognition?.enabled && (
                      <ColumnWithMargins mr={5}>
                        <Pill py={5}>
                          <Icon name="CCTV-Outlined" />
                        </Pill>
                      </ColumnWithMargins>
                    )}
                    <Icon name="Chevron_Right" />
                  </Row>
                </IconWrapper>
              </VehicleWrapper>
            </Pressable>
          ))}
          <VehicleAddSection onPress={navigateToAddVehicle} style="BUTTON" />
        </StyledWrapper>
      </SheetDetailsWrapper>
    </ScrollView>
  );
};

export default MyVehiclesScreen;
